import { AlertCircle, ClipboardList, LoaderCircle, Plus, RefreshCw, Save, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActivityDetailDrawer from "../activity/ActivityDetailDrawer";
import ActivitySummaryCards from "../activity/ActivitySummaryCards";
import ActivityTimeline from "../activity/ActivityTimeline";
import ActivityToolbar from "../activity/ActivityToolbar";
import { createManualActivity, fetchActivities, fetchActivity, fetchActivitySummary } from "../activity/activityApi";
import { categoryOptions, label, severityOptions } from "../activity/activityMeta";
import AdminLayout from "../components/AdminLayout";
import EmptyState from "../components/EmptyState";

const emptyFilters = {
  search: "",
  category: "",
  activity_type: "",
  severity: "",
  actor: "",
  source_type: "",
  date_from: "",
  date_to: "",
  ordering: "-created_at",
  page: 1,
  page_size: 25,
};

const emptyManualForm = {
  title: "",
  description: "",
  category: "system",
  activity_type: "manual_note",
  severity: "info",
  source_label: "",
  source_url: "",
};

function rowsFromPayload(payload) {
  if (Array.isArray(payload)) {
    return { rows: payload, count: payload.length, next: null, previous: null };
  }

  return {
    rows: payload?.results || [],
    count: payload?.count || 0,
    next: payload?.next || null,
    previous: payload?.previous || null,
  };
}

function ManualActivityModal({ isSaving, onCancel, onSubmit }) {
  const [form, setForm] = useState(emptyManualForm);

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event) {
    event.preventDefault();
    onSubmit({
      ...form,
      metadata: {},
      source_label: form.source_label.trim(),
      source_url: form.source_url.trim(),
    });
  }

  return (
    <div className="admin-modal-backdrop">
      <form className="activity-manual-modal" onSubmit={submit}>
        <header>
          <div>
            <span className="admin-eyebrow">Manual Entry</span>
            <h2>Add Activity Note</h2>
          </div>
          <button type="button" onClick={onCancel} disabled={isSaving} aria-label="Close manual activity form">
            <X size={20} />
          </button>
        </header>

        <div className="activity-manual-grid">
          <label>
            Title
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
          </label>
          <label>
            Category
            <select value={form.category} onChange={(event) => change("category", event.target.value)}>
              {categoryOptions.map((value) => <option key={value} value={value}>{label(value)}</option>)}
            </select>
          </label>
          <label>
            Activity Type
            <input required value={form.activity_type} onChange={(event) => change("activity_type", event.target.value)} />
          </label>
          <label>
            Severity
            <select value={form.severity} onChange={(event) => change("severity", event.target.value)}>
              {severityOptions.map((value) => <option key={value} value={value}>{label(value)}</option>)}
            </select>
          </label>
          <label className="activity-manual-wide">
            Description
            <textarea rows="4" value={form.description} onChange={(event) => change("description", event.target.value)} />
          </label>
          <label>
            Source Label
            <input value={form.source_label} onChange={(event) => change("source_label", event.target.value)} />
          </label>
          <label>
            Source URL
            <input value={form.source_url} onChange={(event) => change("source_url", event.target.value)} placeholder="/admin/orders/123" />
          </label>
        </div>

        <footer>
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
          <button type="submit" className="btn" disabled={isSaving}>
            {isSaving ? <LoaderCircle size={16} className="admin-spin" /> : <Save size={16} />}
            {isSaving ? "Saving..." : "Save Activity"}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default function AdminActivityPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(emptyFilters);
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / Number(filters.page_size || 25))), [count, filters.page_size]);

  const loadData = useCallback(async (signal) => {
    setIsLoading(true);
    setError("");
    try {
      const [summaryData, activityPayload] = await Promise.all([
        fetchActivitySummary({ ...filters, signal }),
        fetchActivities({ ...filters, signal }),
      ]);
      const normalized = rowsFromPayload(activityPayload);
      setSummary(summaryData);
      setActivities(normalized.rows);
      setCount(normalized.count);
      setNext(normalized.next);
      setPrevious(normalized.previous);
    } catch (requestError) {
      if (requestError.name !== "AbortError") {
        setError(requestError.message || "Activity could not be loaded.");
      }
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  function setFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value, page: field === "page" ? value : 1 }));
  }

  async function openActivity(activity) {
    setSelectedActivity(activity);
    try {
      setSelectedActivity(await fetchActivity(activity.id));
    } catch (requestError) {
      setError(requestError.message || "Activity details could not be loaded.");
    }
  }

  async function saveManual(payload) {
    setIsSaving(true);
    setError("");
    try {
      const created = await createManualActivity(payload);
      setIsManualOpen(false);
      setSelectedActivity(created);
      setSuccess("Activity note saved.");
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Activity note could not be saved.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AdminLayout>
      <section className="admin-page activity-page">
        <header className="admin-page-heading">
          <div>
            <span className="admin-eyebrow">Business History</span>
            <h1>Activity</h1>
            <p>Search orders, payments, customers, inventory, deliveries, messages, coupons, specials, calendar, menu, system, and security changes from one audit trail.</p>
          </div>
          <div className="activity-heading-actions">
            <button type="button" className="btn btn--secondary" onClick={() => loadData()}>
              <RefreshCw size={17} /> Refresh
            </button>
            <button type="button" className="btn" onClick={() => setIsManualOpen(true)}>
              <Plus size={17} /> Add Note
            </button>
          </div>
        </header>

        {error && <div className="admin-menu-error"><AlertCircle size={18} /><span>{error}</span></div>}
        {success && <div className="inventory-success">{success}</div>}

        <ActivitySummaryCards summary={summary} />
        <ActivityToolbar filters={filters} onChange={setFilter} onClear={() => setFilters(emptyFilters)} />

        {isLoading ? (
          <div className="inventory-loading"><LoaderCircle size={22} className="admin-spin" /> Loading activity...</div>
        ) : activities.length ? (
          <>
            <ActivityTimeline activities={activities} onOpen={openActivity} />
            <div className="activity-pagination">
              <span>{count} total records</span>
              <div>
                <button type="button" className="btn btn--secondary" disabled={!previous} onClick={() => setFilter("page", Math.max(1, Number(filters.page) - 1))}>Previous</button>
                <strong>Page {filters.page} of {totalPages}</strong>
                <button type="button" className="btn btn--secondary" disabled={!next} onClick={() => setFilter("page", Number(filters.page) + 1)}>Next</button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            icon={ClipboardList}
            title="No activity found"
            description="Adjust the filters or add a manual note to start the business audit trail."
            actionLabel="Add Note"
            onAction={() => setIsManualOpen(true)}
          />
        )}
      </section>

      <ActivityDetailDrawer activity={selectedActivity} onClose={() => setSelectedActivity(null)} onNavigate={(url) => navigate(url)} />
      {isManualOpen && <ManualActivityModal isSaving={isSaving} onCancel={() => !isSaving && setIsManualOpen(false)} onSubmit={saveManual} />}
    </AdminLayout>
  );
}